// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components/TableFornecedor/layoutDasColunas.ts

/**
 * A largura e o alinhamento das colunas da grade, e só isso: ordem, quais aparecem, conteúdo das
 * células, cabeçalho e altura da linha moram em `TableFornecedor.tsx`.
 *
 * Três armadilhas: `width` desliga o `flexGrow`, cujo piso vai em `minWidth`; `alinhamento` só
 * onde já está escrito, porque ligá-lo torna a célula uma caixa flexível; `texto` só em Produto.
 */

export type AlinhamentoDaColuna = 'left' | 'center' | 'right';

export type ComportamentoDoTexto = 'quebra' | 'corta';

export type ChaveDaColuna =
  | 'seleção'
  | 'produto'
  | 'loja'
  | 'estoque'
  | 'margem objetiva'
  | 'custo atual'
  | 'novo custo'
  | 'verba de sell-in'
  | 'novo preço'
  | 'preço vigente'
  | 'preço concorrência'
  | 'preço ideal de compra'
  | 'aprovação';

export interface LayoutDaColuna {
  chave: ChaveDaColuna;
  titulo: string;
  width?: number;
  minWidth?: number;
  flexGrow?: number;
  alinhamento?: AlinhamentoDaColuna;
  /** Só a coluna Produto usa. Ausente vale como `'quebra'`. */
  texto?: ComportamentoDoTexto;
}

// NEG-496: a soma das larguras tem de caber em 1.493px a 1600px, zoom 100%, com a barra de rolagem
// vertical do Windows (seção 2 do card). Desenho medido no protótipo (seção 5): detalhe em 1.490px,
// busca em 1.440px, as duas ≤ 1.493. Nada de coluna escondida nem fonte menor — a folga veio de
// estreitar e empilhar conteúdo dentro da célula (ver TableFornecedor.module.scss).
//
// Em tela mais larga que 1600 px, a sobra vai para as colunas numéricas, e não para o nome do
// produto, que já quebra em duas linhas com o nome inteiro na dica do mouse. Cada coluna numérica
// declara o piso em `minWidth` e cresce na proporção dele (`flexGrow` igual ao piso), e assim nenhuma
// fica presa no piso enquanto as outras crescem.
const cresceDesde = (piso: number): Pick<LayoutDaColuna, 'minWidth' | 'flexGrow'> => ({ minWidth: piso, flexGrow: piso });

export const LAYOUT_DAS_COLUNAS: LayoutDaColuna[] = [
  { chave: 'seleção', titulo: '(a caixa de seleção)', width: 46, alinhamento: 'center' },

  { chave: 'produto', titulo: 'Produto', width: 276, texto: 'quebra' },

  { chave: 'loja', titulo: 'Loja — "Rede" na visão por rede', width: 76, alinhamento: 'left' },
  { chave: 'estoque', titulo: 'Estoque', ...cresceDesde(70) },
  { chave: 'margem objetiva', titulo: 'Margem objetiva', ...cresceDesde(80) },

  // `minWidth` sem `flexGrow` o rsuite-table ignora e aplica 100px por padrão: o piso só vale junto.
  { chave: 'custo atual', titulo: 'Custo atual', ...cresceDesde(100) },

  { chave: 'novo custo', titulo: 'Novo custo', width: 116 },

  // Larga o bastante para a MAIOR linha de subtexto, não para o campo. 8px de folga sobre o
  // conteúdo mais longo medido (271px), para a diferença de fonte entre navegadores/SO.
  { chave: 'verba de sell-in', titulo: 'Verba de sell-in', width: 280 },

  { chave: 'novo preço', titulo: 'Novo preço', width: 116 },
  { chave: 'preço vigente', titulo: 'Preço vigente', ...cresceDesde(88) },
  { chave: 'preço concorrência', titulo: 'Preço concorrência', ...cresceDesde(94) },
  { chave: 'preço ideal de compra', titulo: 'Preço ideal de compra', ...cresceDesde(98) },
  { chave: 'aprovação', titulo: '(aprovar ou reprovar o item)', width: 50, alinhamento: 'left' },
];

/** Estoura em vez de devolver vazio: sem layout a coluna sai estreita na tela, e não como falha. */
export const layoutDaColuna = (chave: ChaveDaColuna): LayoutDaColuna => {
  const layout = LAYOUT_DAS_COLUNAS.find(candidato => candidato.chave === chave);

  if (!layout) throw new Error(`a coluna "${chave}" não tem layout declarado em layoutDasColunas.ts`);

  return layout;
};

export interface DimensoesDaColuna {
  width?: number;
  minWidth?: number;
  flexGrow?: number;
  align?: AlinhamentoDaColuna;
}

/** Campo que a lista não declara sai daqui AUSENTE, e não com valor padrão. */
export const dimensoesDaColuna = (chave: ChaveDaColuna): DimensoesDaColuna => {
  const { width, minWidth, flexGrow, alinhamento } = layoutDaColuna(chave);

  return { width, minWidth, flexGrow, align: alinhamento };
};
